"use client";

import { Input } from "@nextui-org/input";
import { useDisclosure } from "@nextui-org/modal";
import Konva from "konva";
import { KonvaEventObject, NodeConfig,Node } from 'konva/lib/Node';
import { Stage } from 'konva/lib/Stage';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useTranslations } from "next-intl";
import { useRouter } from 'next/navigation';
import { ChangeEvent, DragEvent, useCallback, useEffect, useRef, useState } from "react";
import { ComfyUIApi } from './comfyui_api';
import { ControlPanel } from "./ControlPanel";
import { CustomModal } from "./CustomModal";
import workflow from './text-remove-workflow-api.json';
import dynamic from 'next/dynamic';
import { ComponentType } from 'react';

// 必须使用动态导入 ImageCanvas 组件，否则vercel编译报错
// https://github.com/konvajs/react-konva#usage-with-nextjs
// @ts-ignore
const ImageCanvas: ComponentType = dynamic(() => import('./ImageCanvas'), {
  ssr: false,
});

export default function ImageEditor() {
  const t = useTranslations()
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(30);
  const [imageProps, setImageProps] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });
  const stageRef = useRef<Stage | null>(null);
  const hiddenContainerRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [originalImageBase64, setOriginalImageBase64] = useState<string | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const controlsHeight = 100;
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isOverImage, setIsOverImage] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState<'draw' | 'pan'>('draw');
  const { isOpen: isErrorModalOpen, onOpen: onErrorModalOpen, onClose: onErrorModalClose } = useDisclosure();
  const { isOpen: isClearCanvasModalOpen, onOpen: onClearCanvasModalOpen, onClose: onClearCanvasModalClose } = useDisclosure();
  const [errorMessage, setErrorMessage] = useState("");
  const [originalFileName, setOriginalFileName] = useState<string>('image');
  const [modeChangeMessage, setModeChangeMessage] = useState<string | null>(null);
  const [showOriginalImage, setShowOriginalImage] = useState(false);
  const router = useRouter();
  const [originalImageType, setOriginalImageType] = useState<string>('image/png');
  const [showCursor, setShowCursor] = useState(false);
  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [originalImageInfo, setOriginalImageInfo] = useState<{
    image: HTMLImageElement | null;
    imageProps: typeof imageProps;
    scale: number;
    position: { x: number; y: number };
  } | null>(null);

  const [generatedImageInfo, setGeneratedImageInfo] = useState<{
    image: HTMLImageElement | null;
    imageProps: typeof imageProps;
    scale: number;
    position: { x: number; y: number };
  } | null>(null);

  const [isFirstGeneration, setIsFirstGeneration] = useState(true);

  useEffect(() => {
    const updateSize = () => {
      setStageSize({
        width: window.innerWidth,
        height: window.innerHeight - controlsHeight
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const loadAndScaleImage = (imgSrc: string | File, keepCurrentProps: boolean = false) => {
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      if (typeof imgSrc === 'string') {
        img.src = imgSrc;
      } else {
        img.src = URL.createObjectURL(imgSrc);
      }

      img.onload = async () => {
        if (keepCurrentProps) {
          // 保持当前的图片属性
          setImage(img);
        } else {
          const scale = Math.min(
            (stageSize.width - 40) / img.width, 
            (stageSize.height - 40) / img.height
          );
          const width = img.width * scale;
          const height = img.height * scale;
          const x = (stageSize.width - width) / 2;
          const y = (stageSize.height - height) / 2;
          setImageProps({ width, height, x, y });
          setImage(img);
          setScale(1);
          setPosition({ x: 0, y: 0 });
        }
        
        const base64 = await getImageBase64(img.src);
        setOriginalImageBase64(base64);
        setIsImageLoaded(true);
        setOriginalImageType(imgSrc instanceof File ? imgSrc.type : 'image/png');
        resolve();
      };
    });
  };

  const handleFileUpload = async (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setOriginalFileName(file.name.split('.')[0] || '');
      await loadAndScaleImage(file);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const getImageBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fullResult = reader.result as string;
        const base64 = fullResult.split(',')[1];
        resolve(base64 || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const isPointInImage = (x: number, y: number) => {
    const scaledX = (x - position.x) / scale;
    const scaledY = (y - position.y) / scale;
    return (
      scaledX >= imageProps.x &&
      scaledX <= imageProps.x + imageProps.width &&
      scaledY >= imageProps.y &&
      scaledY <= imageProps.y + imageProps.height
    );
  };

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (mode === 'draw') {
      const stage = e.target.getStage();
      if (!stage) return;
      const layer = stage.findOne('Layer') as Konva.Layer;
      const pos = layer.getRelativePointerPosition();
      if (!pos) return;
      if (isPointInImage(pos.x, pos.y)) {
        setIsDrawing(true);
        setShowCursor(true);
        setLines([
          ...lines,
          {
            points: [pos.x, pos.y],
            strokeWidth: brushSize / stageScale, // 调整笔刷大小
            stroke: "rgba(0, 128, 0, 0.7)",
            tension: 0.5,
            lineCap: 'round',
            lineJoin: 'round',
          },
        ]);
      }
    }
  };

  const handleMouseMove = useCallback((e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    const layer = stage.findOne('Layer') as Konva.Layer;
    const pos = layer.getRelativePointerPosition();
    if (!pos) return;
    const inImage = isPointInImage(pos.x, pos.y);
    setIsOverImage(inImage);
    setCursorPosition({ x: e.evt.clientX, y: e.evt.clientY });

    if (isDrawing && inImage && mode === 'draw') {
      let lastLine = lines[lines.length - 1];
      lastLine.points = lastLine.points.concat([pos.x, pos.y]);
      lastLine.strokeWidth = brushSize / stageScale; // 更新笔刷大小
      lines.splice(lines.length - 1, 1, lastLine);
      setLines([...lines]);
    }
  }, [isDrawing, lines, stageScale, mode, isPointInImage, brushSize]);

  const handleMouseUp = () => {
    setIsDrawing(false);
    setShowCursor(false);
  };

  const handleGenerateImage = async () => {
    if (!image || !originalImageBase64) return;

    if (lines.length === 0) {
      setErrorMessage(t("Playground.errorNoMask"));
      onErrorModalOpen();
      return;
    }

    setIsGenerating(true);

    // 如果是第一次生成，保存当前图片信息为原始图片信息
    if (isFirstGeneration) {
      setOriginalImageInfo({
        image,
        imageProps,
        scale: stageScale,
        position: stagePosition,
      });
    }

    const hiddenContainer = hiddenContainerRef.current;
    if (!hiddenContainer) {
      setIsGenerating(false);
      return;
    }

    const newStage = new Konva.Stage({
      container: hiddenContainer,
      width: image.width,
      height: image.height,
    });

    const newLayer = new Konva.Layer();
    newStage.add(newLayer);

    const konvaImage = new Konva.Image({
      image: image,
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    });
    newLayer.add(konvaImage);

    const scaleX = image.width / imageProps.width;
    const scaleY = image.height / imageProps.height;

    lines.forEach((line) => {
      const adjustedPoints = line.points.map((point: number, index: number) => {
        if (index % 2 === 0) {
          // X坐标
          return (point - imageProps.x) * scaleX;
        } else {
          // Y坐标
          return (point - imageProps.y) * scaleY;
        }
      });

      const konvaLine = new Konva.Line({
        points: adjustedPoints,
        stroke: "rgba(0, 0, 0, 1)",
        strokeWidth: line.strokeWidth * scaleX,
        tension: 0.5,
        lineCap: "round",
        lineJoin: "round",
        globalCompositeOperation: "destination-out",
      });
      newLayer.add(konvaLine);
    });

    newLayer.draw();

    const dataURL = newStage.toDataURL({
      pixelRatio: 1,
      mimeType: "image/png",
      quality: 1,
    });

    const maskBase64 = dataURL.split(",")[1];

    const comfyApi = new ComfyUIApi();
    
    try {
      if (originalImageBase64 && maskBase64 && workflow) {
        const result = await comfyApi.processImage(
          originalImageBase64 as string, 
          maskBase64, 
          workflow, 
          (executionData: any) => {
            console.log(t("Playground.executionProgress"), executionData);
            setProgress(executionData.progress || 0);
          }
        );
        if (result) {
          await loadAndScaleImage(result, true);
          setLines([]);
          setProgress(0);
          
          // 更新生成的图片信息
          setGeneratedImageInfo({
            image,
            imageProps,
            scale: stageScale,
            position: stagePosition,
          });

          // 如果不是第一次生成，更新原始图片信息
          if (!isFirstGeneration) {
            setOriginalImageInfo(generatedImageInfo);
          }

          // 更新原始图片的base64
          setOriginalImageBase64(await getImageBase64(result));

          // 设置为非第一次生成
          setIsFirstGeneration(false);
        }
      } else {
        console.error(t("Playground.missingParameters"));
      }
    } catch (error) {
      console.error(t("Playground.errorProcessingImage"), error);
      setProgress(0);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUndo = () => {
    if (lines.length > 0) {
      const newLines = [...lines];
      newLines.pop();
      setLines(newLines);
    }
  };

  const handleClearAll = () => {
    setLines([]);
  };

  const handleZoom = (newScale: number) => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stageScale;
    const stageCenter = {
      x: stage.width() / 2,
      y: stage.height() / 2,
    };

    const newPos = {
      x: (stagePosition.x - stageCenter.x) * (newScale / oldScale) + stageCenter.x,
      y: (stagePosition.y - stageCenter.y) * (newScale / oldScale) + stageCenter.y,
    };

    setStageScale(newScale);
    setStagePosition(newPos);
  };

  const handleZoomIn = () => {
    handleZoom(stageScale * 1.1);
  };

  const handleZoomOut = () => {
    handleZoom(stageScale / 1.1);
  };

  const handleDragMove = (e: KonvaEventObject<DragEvent>) => {
    if (mode === 'pan') {
      e.evt.preventDefault();
      e.evt.stopPropagation();
      const stage = e.target.getStage();
      if (stage) {
        setStagePosition({
          x: stage.x(),
          y: stage.y(),
        });
      }
    }
  };

  const toggleMode = () => {
    const newMode = mode === 'draw' ? 'pan' : 'draw';
    setMode(newMode);
    setModeChangeMessage(t(`Playground.${newMode}ModeMessage`));
    setTimeout(() => setModeChangeMessage(null), 2000);
  };

  const handleWheel = (e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stageScale;
    const pointerPosition = stage.getPointerPosition();
    if (!pointerPosition) return;

    const mousePointTo = {
      x: (pointerPosition.x - stagePosition.x) / oldScale,
      y: (pointerPosition.y - stagePosition.y) / oldScale,
    };

    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;

    setStageScale(newScale);
    setStagePosition({
      x: pointerPosition.x - mousePointTo.x * newScale,
      y: pointerPosition.y - mousePointTo.y * newScale,
    });
  };

  const handleClearCanvas = () => {
    onClearCanvasModalOpen();
  };

  const confirmClearCanvas = () => {
    setImage(null);
    setLines([]);
    setIsImageLoaded(false);
    setOriginalImageBase64(null);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setMode('draw');
    onClearCanvasModalClose();
  };

  const handleDownload = () => {
    if (!image || !originalImageBase64) return;

    // 创建一个新的离屏 canvas
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = image.width;
    offscreenCanvas.height = image.height;
    const ctx = offscreenCanvas.getContext('2d');
    if (!ctx) return;

    // 绘制原始图片
    ctx.drawImage(image, 0, 0, image.width, image.height);

    // 绘制所有线条
    lines.forEach((line) => {
      ctx.beginPath();
      ctx.strokeStyle = line.stroke;
      ctx.lineWidth = line.strokeWidth * (image.width / imageProps.width); // 调整线宽以匹配原始图片大小
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      for (let i = 0; i < line.points.length; i += 2) {
        const x = (line.points[i] - imageProps.x) * (image.width / imageProps.width);
        const y = (line.points[i + 1] - imageProps.y) * (image.height / imageProps.height);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    });

    // 获取 canvas 的 blob 数据
    offscreenCanvas.toBlob((blob) => {
      if (blob) {
        // 创建下载链接
        const link = document.createElement('a');
        const fileExtension = originalImageType.split('/')[1];
        link.download = `${originalFileName}-edited.${fileExtension}`;
        link.href = URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href); // 清理创建的 URL
      }
    }, originalImageType, 1.0);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file && file.type.startsWith('image/')) {
        handleFileUpload(file);
      }
    }
  };

  const handleTouchStart = (e: KonvaEventObject<TouchEvent>) => {
    if (mode === 'draw') {
      const stage = e.target.getStage();
      if (!stage) return;
      const layer = stage.findOne('Layer') as Konva.Layer;
      const pos = layer.getRelativePointerPosition();
      if (!pos) return;
      if (isPointInImage(pos.x, pos.y)) {
        setIsDrawing(true);
        setShowCursor(true);
        setLines([
          ...lines,
          {
            points: [pos.x, pos.y],
            strokeWidth: brushSize / stageScale, // 调整笔刷大小
            stroke: "rgba(0, 128, 0, 0.7)",
            tension: 0.5,
            lineCap: 'round',
            lineJoin: 'round',
          },
        ]);
      }
    }
  };

  const handleTouchMove = useCallback((e: KonvaEventObject<TouchEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    const layer = stage.findOne('Layer') as Konva.Layer;
    const pos = layer.getRelativePointerPosition();
    if (!pos) return;
    const inImage = isPointInImage(pos?.x ?? 0, pos?.y ?? 0);
    setIsOverImage(inImage);
    setCursorPosition({ x: e.evt.touches[0]?.clientX ?? 0, y: e.evt.touches[0]?.clientY ?? 0 });

    if (isDrawing && inImage && mode === 'draw') {
      let lastLine = lines[lines.length - 1];
      lastLine.points = lastLine.points.concat([pos?.x ?? 0, pos?.y ?? 0]);
      lastLine.strokeWidth = brushSize / stageScale; // 更新笔刷大小
      lines.splice(lines.length - 1, 1, lastLine);
      setLines([...lines]);
    }
  }, [isDrawing, lines, stageScale, mode, isPointInImage, brushSize]);

  const handleTouchEnd = () => {
    setIsDrawing(false);
    setShowCursor(false);
  };

  const toggleOriginalImage = () => {
    if (showOriginalImage) {
      // 切换回生成的图片
      if (generatedImageInfo) {
        setImage(generatedImageInfo.image);
        setImageProps(generatedImageInfo.imageProps);
        setStageScale(generatedImageInfo.scale);
        setStagePosition(generatedImageInfo.position);
      }
    } else {
      // 切换到原始图片
      if (originalImageInfo) {
        setImage(originalImageInfo.image);
        setImageProps(originalImageInfo.imageProps);
        setStageScale(originalImageInfo.scale);
        setStagePosition(originalImageInfo.position);
      }
    }
    setShowOriginalImage(!showOriginalImage);
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      {/* 返回首页按钮 */}
      <button title="Back to Home"
        className="absolute top-4 left-4 z-20 bg-white rounded-full p-2 shadow-md"
        onClick={() => router.push('/')}
      >
        <ArrowLeftIcon className="w-6 h-6" />
      </button>

      {/* 右侧按钮组 */}
      {/* <div className="absolute top-4 right-4 z-20 flex space-x-2">
        <button
          className="bg-white rounded-full p-2 shadow-md"
          onClick={toggleOriginalImage}
          disabled={!originalImageInfo || !generatedImageInfo}
        >
          {showOriginalImage ? <EyeOffIcon className="w-6 h-6" /> : <EyeIcon className="w-6 h-6" />}
        </button>
      </div> */}

      {!isImageLoaded ? (
        <div className="flex items-center justify-center h-full">
          <div 
            className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h2 className="text-2xl mb-4">{t("Playground.uploadImagePrompt")}</h2>
            <p className="text-sm text-gray-500 mb-4">{t("Playground.dragDropPrompt")}</p>
            <Input
              title={t("Playground.uploadButtonLabel")}
              type="file"
              accept="image/*"
              placeholder={t("Playground.uploadPlaceholder")}
              onChange={handleInputChange}
              className="max-w-xs mx-auto"
            />
          </div>
        </div>
      ) : (
        <>
          {/* 显示原始图片或编辑后的图片 */}
          {showOriginalImage && originalImageBase64 ? (
            <img
              src={`data:image/png;base64,${originalImageBase64}`}
              alt="Original"
              className="absolute top-0 left-0 w-full h-full object-contain"
            />
          ) : (
            <ImageCanvas
              // @ts-ignore
              image={image}
              lines={lines}
              scale={stageScale}
              position={stagePosition}
              mode={mode}
              brushSize={brushSize}
              stageRef={stageRef}
              stageSize={stageSize}
              imageProps={imageProps}
              handleMouseDown={handleMouseDown}
              handleMouseMove={handleMouseMove}
              handleMouseUp={handleMouseUp}
              handleWheel={handleWheel}
              handleDragMove={handleDragMove}
              handleTouchStart={handleTouchStart}
              handleTouchMove={handleTouchMove}
              handleTouchEnd={handleTouchEnd}
            />
          )}
          <div 
            className="absolute pointer-events-none z-10"
            style={{
              width: `${brushSize / stageScale}px`, // 调整光标大小
              height: `${brushSize / stageScale}px`, // 调整光标大小
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 128, 0, 0.3)',
              border: '1px solid rgba(0, 128, 0, 0.7)',
              transform: 'translate(-50%, -50%)',
              display: isImageLoaded && isOverImage && mode === 'draw' && showCursor ? 'block' : 'none',
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`
            }}
          />
        </>
      )}
      <ControlPanel
        isImageLoaded={isImageLoaded}
        brushSize={brushSize}
        setBrushSize={setBrushSize}
        handleUndo={handleUndo}
        handleClearAll={handleClearAll}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        toggleMode={toggleMode}
        mode={mode}
        handleClearCanvas={handleClearCanvas}
        handleGenerateImage={handleGenerateImage}
        isGenerating={isGenerating}
        handleDownload={handleDownload}
        lines={lines}
        controlsHeight={controlsHeight}
      />
      <div ref={hiddenContainerRef} className="hidden"></div>
      <CustomModal
        isOpen={isErrorModalOpen}
        onClose={onErrorModalClose}
        title={t("Playground.errorTitle")}
        actions={[]}
        content={errorMessage}
      />
      <CustomModal
        isOpen={isClearCanvasModalOpen}
        onClose={onClearCanvasModalClose}
        title={t("Playground.clearCanvasTitle")}
        content={t("Playground.clearCanvasContent")}
        actions={[
          { label: t("Playground.clearCanvasConfirm"), color: "danger", onClick: confirmClearCanvas },
          { label: t("Playground.clearCanvasCancel"), color: "default", onClick: onClearCanvasModalClose }
        ]}
      />
      {modeChangeMessage && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full transition-opacity duration-300">
          {modeChangeMessage}
        </div>
      )}
    </div>
  );
}